import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { FileText, TrendingUp, Phone, Calculator, DollarSign, Users } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

interface DashboardStats {
  totalDeals: number;
  activeInvestments: number;
  pendingQuotes: number;
  totalValue: number;
}

export function AdminDashboard() {
  const [stats, setStats] = useState<DashboardStats>({
    totalDeals: 0,
    activeInvestments: 0,
    pendingQuotes: 0,
    totalValue: 0,
  });
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        // Get deals count
        const { count: dealsCount } = await supabase
          .from('deals')
          .select('*', { count: 'exact', head: true });

        // Get investments count
        const { count: investmentsCount } = await supabase
          .from('investments')
          .select('*', { count: 'exact', head: true })
          .eq('status', 'Available');

        // Get pending quotes count
        const { count: quotesCount } = await supabase
          .from('quotes')
          .select('*', { count: 'exact', head: true })
          .in('status', ['draft', 'sent']);

        // Get total value of deals
        const { data: dealsData } = await supabase
          .from('deals')
          .select('sales_price')
          .not('sales_price', 'is', null);

        const totalValue = dealsData?.reduce((sum, deal) => 
          sum + (deal.sales_price || 0), 0) || 0;

        setStats({
          totalDeals: dealsCount || 0,
          activeInvestments: investmentsCount || 0,
          pendingQuotes: quotesCount || 0,
          totalValue,
        });
      } catch (error) {
        console.error('Error fetching dashboard stats:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchStats();
  }, []);

  const statCards = [
    {
      title: "Total Deals",
      value: stats.totalDeals,
      icon: FileText,
      color: "text-blue-600",
    },
    {
      title: "Active Investments",
      value: stats.activeInvestments,
      icon: TrendingUp,
      color: "text-green-600",
    },
    {
      title: "Pending Quotes",
      value: stats.pendingQuotes,
      icon: Phone,
      color: "text-orange-600",
    },
    {
      title: "Total Portfolio Value",
      value: `$${stats.totalValue.toLocaleString()}`,
      icon: DollarSign,
      color: "text-purple-600",
    },
  ];

  if (isLoading) {
    return (
      <div className="space-y-6">
        <h1 className="text-3xl font-bold">Dashboard</h1>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[...Array(4)].map((_, i) => (
            <Card key={i} className="animate-pulse">
              <CardHeader className="h-24 bg-muted"></CardHeader>
              <CardContent className="h-16 bg-muted/50"></CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold">Dashboard</h1>
        <p className="text-muted-foreground">Welcome to your admin portal</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((stat, index) => (
          <Card key={index} className="hover:shadow-md transition-shadow">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                {stat.title}
              </CardTitle>
              <stat.icon className={`h-5 w-5 ${stat.color}`} />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stat.value}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calculator className="h-5 w-5" />
            Quick Actions
          </CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="p-4 hover:bg-muted/50 transition-colors cursor-pointer">
            <div className="flex items-center gap-3">
              <FileText className="h-6 w-6 text-blue-600" />
              <div>
                <h3 className="font-medium">New Contract</h3>
                <p className="text-sm text-muted-foreground">Add a new land contract</p>
              </div>
            </div>
          </Card>
          
          <Card className="p-4 hover:bg-muted/50 transition-colors cursor-pointer">
            <div className="flex items-center gap-3">
              <TrendingUp className="h-6 w-6 text-green-600" />
              <div>
                <h3 className="font-medium">Create Investment</h3>
                <p className="text-sm text-muted-foreground">Promote contract to investment</p>
              </div>
            </div>
          </Card>
          
          <Card className="p-4 hover:bg-muted/50 transition-colors cursor-pointer">
            <div className="flex items-center gap-3">
              <Calculator className="h-6 w-6 text-purple-600" />
              <div>
                <h3 className="font-medium">Calculate Values</h3>
                <p className="text-sm text-muted-foreground">Use investment calculator</p>
              </div>
            </div>
          </Card>
        </CardContent>
      </Card>
    </div>
  );
}